'use client';

// @boty/ui - MarkdownContent component
// Renders markdown text using MUI-compatible typography components

import type { MouseEvent, ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import type { SxProps, Theme } from '@mui/material/styles';

export interface MarkdownContentProps {
  /** Markdown text to render */
  children: string;
  /** Additional styles */
  sx?: SxProps<Theme>;
  /** Callback when a link is clicked. If provided, prevents default navigation. */
  onLinkClick?: ((url: string) => void) | undefined;
}

/**
 * MarkdownContent - Renders markdown text with MUI-styled elements
 *
 * Maps markdown elements to MUI components for consistent styling:
 * - Headings → compact Typography variants
 * - Paragraphs → body1 Typography
 * - Links → MUI Link (opens in new tab)
 * - Code blocks → styled Box with monospace font
 * - Inline code → styled span with background
 * - Lists → native HTML with proper spacing
 *
 * Safe by default — react-markdown does not render raw HTML.
 */
export function MarkdownContent({ children, sx, onLinkClick }: MarkdownContentProps) {
  return (
    <Box
      sx={{
        // Reset spacing for markdown block elements
        '& > *:first-of-type': { mt: 0 },
        '& > *:last-child': { mb: 0 },
        wordBreak: 'break-word',
        ...sx,
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Headings — compact variants for chat context
          h1: ({ children: c }) => (
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {c}
            </Typography>
          ),
          h2: ({ children: c }) => (
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {c}
            </Typography>
          ),
          h3: ({ children: c }) => (
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              {c}
            </Typography>
          ),
          h4: ({ children: c }) => (
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              {c}
            </Typography>
          ),

          // Paragraphs
          p: ({ children: c }) => (
            <Typography variant="body1" sx={{ mb: 1 }}>
              {c}
            </Typography>
          ),

          // Links — intercept clicks if onLinkClick provided, otherwise open in new tab
          a: ({ href, children: c }) => {
            if (onLinkClick && href) {
              const handleClick = (e: MouseEvent) => {
                e.preventDefault();
                onLinkClick(href);
              };
              return (
                <Link
                  href={href}
                  onClick={handleClick}
                  underline="hover"
                  sx={{ cursor: 'pointer' }}
                >
                  {c}
                </Link>
              );
            }
            return (
              <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover">
                {c}
              </Link>
            );
          },

          // Code blocks
          pre: ({ children: c }) => (
            <Box
              component="pre"
              sx={{
                bgcolor: 'action.hover',
                borderRadius: 1,
                p: 1.5,
                my: 1,
                overflowX: 'auto',
                fontSize: '0.85em',
                fontFamily: 'monospace',
              }}
            >
              {c}
            </Box>
          ),

          // Inline code
          code: ({ children: c, ...props }) => {
            // If wrapped in <pre>, render plain (pre handles styling)
            const isBlock = props.node?.position?.start.line !== props.node?.position?.end.line ||
              (props.className && props.className.includes('language-'));

            if (isBlock) {
              return <code>{c}</code>;
            }

            return (
              <Box
                component="code"
                sx={{
                  bgcolor: 'action.hover',
                  borderRadius: 0.5,
                  px: 0.5,
                  py: 0.25,
                  fontSize: '0.85em',
                  fontFamily: 'monospace',
                }}
              >
                {c}
              </Box>
            );
          },

          // Lists
          ul: ({ children: c }) => (
            <Box component="ul" sx={{ pl: 2.5, my: 0.5 }}>
              {c}
            </Box>
          ),
          ol: ({ children: c }) => (
            <Box component="ol" sx={{ pl: 2.5, my: 0.5 }}>
              {c}
            </Box>
          ),
          li: ({ children: c }: { children?: ReactNode }) => (
            <Typography component="li" variant="body1" sx={{ mb: 0.25 }}>
              {c}
            </Typography>
          ),

          // Blockquote
          blockquote: ({ children: c }) => (
            <Box
              component="blockquote"
              sx={{
                borderInlineStart: '3px solid',
                borderColor: 'divider',
                pl: 1.5,
                my: 1,
                color: 'text.secondary',
              }}
            >
              {c}
            </Box>
          ),

          // Strong and emphasis — use inline elements
          strong: ({ children: c }) => <strong>{c}</strong>,
          em: ({ children: c }) => <em>{c}</em>,
        }}
      >
        {children}
      </ReactMarkdown>
    </Box>
  );
}

MarkdownContent.displayName = 'MarkdownContent';
